
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { configStore, documentStore, uiStore } from "@/store/store";
import { fetchAvailableDocuments } from "@/lib/document-chooser/document-chooser";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import lodash from "lodash";
import NotificationLogger from "@/lib/notifications/notifications";
import IconButton from "@/components/UI/IconButton.vue";

import Icon from "@/components/UI/Icon.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import TopBarSection from "@/components/Reusable/AppLayout/TopBar/TopBarSection.vue";
import SearchInput from "@/components/Reusable/CustomInputs/SearchInput.vue";
import Dialog from "@/components/UI/Dialog.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import DocumentChooserCreateDialog from "@/components/Builder/TopBar/DocumentChooser/DocumentChooserCreateDialog.vue";
import DocumentChooserItem from "@/components/Builder/TopBar/DocumentChooser/DocumentChooserItem.vue";
import DocumentChooserFilters from "@/components/Builder/TopBar/DocumentChooser/DocumentChooserFilters.vue";
import defensiveCopy from "@/util/defensive-copy";
import { decodeHtmlEntities } from "@/util/dom";

@Component({
  components: {
    DocumentChooserFilters,
    NiceButton,
    DropdownInput,
    TextInput,
    Dialog,
    Icon,
    Dropdown,
    SearchInput,
    TopBarSection,
    DocumentChooserItem,
    DocumentChooserCreateDialog,
    IconButton
  }
})
export default class DocumentChooser extends Vue {
  search: string | null = "";

  open = false;
  createOpen = false;
  isLoading = false;
  postToShowFirstId: number | null = null;

  sortBy = "";
  postType = "";

  @Watch("open")
  onOpenChange() {
    this.postToShowFirstId = null;
  }

  @Watch("sortBy")
  onSortChange() {
    this.searchContentInWp();
  }

  @Watch("postType")
  onPostTypeChange() {
    this.searchContentInWp();
  }

  get documentTitle() {
    if (this.currentDocument.documentMeta.titleLabel) {
      return decodeHtmlEntities(this.currentDocument.documentMeta.titleLabel);
    }

    return `ID: ${this.currentDocument.documentMeta.id}`;
  }

  get unsavedChangesPresent() {
    return uiStore.unsavedChangesPresent;
  }

  get availableDocuments() {
    const documents = [...configStore.availableDocuments];

    if (this.postToShowFirstId === null) return documents;

    return documents.sort((a, b) => {
      if (a.id === this.postToShowFirstId) return -1;
      if (b.id === this.postToShowFirstId) return 1;
      return 0;
    });
  }

  get builderMode() {
    return configStore.builderMode;
  }

  get currentDocument() {
    return documentStore.document;
  }

  // Average typing speed is a char every 300 ms
  debouncedSearchContentInWp = lodash.debounce(this.searchContentInWp, 200); // this feels slow tho

  handleSearch(searchValue: string) {
    this.postToShowFirstId = null;
    this.search = searchValue;
    this.debouncedSearchContentInWp();
  }

  async searchContentInWp() {
    this.isLoading = true;

    pipe(
      await fetchAvailableDocuments({
        search: this.search || "",
        postType: this.postType,
        sortBy: this.sortBy
      }),
      fold(
        errors => {
          this.isLoading = false;
          errors.forEach(e => NotificationLogger.log.message(e.msg));
        },
        availableDocuments => {
          configStore.setAvailableDocuments(availableDocuments);
          this.isLoading = false;
        }
      )
    );

    this.scrollToTop();
  }

  afterCreation(createdPost: {
    postId: number;
    postTitle: string;
    postType: string;
  }) {
    this.search = "";
    this.postToShowFirstId = createdPost.postId;

    if (this.postType && this.postType !== createdPost.postType) {
      this.postType = "";
    } else {
      this.searchContentInWp();
    }
  }

  scrollToTop() {
    const scrollable = this.$refs.scrollable as Vue & {
      $el: HTMLDivElement;
    };

    this.$nextTick(() => {
      scrollable.$el.scrollTop = 0;
    });
  }

  updateTitleInPlace(updatedItem: {
    title?: string;
    label?: string;
    id: number;
  }) {
    const index = this.availableDocuments.findIndex(
      item => item.id === updatedItem.id
    );
    const item = this.availableDocuments[index];

    if (index === -1) return;

    const availableDocuments = defensiveCopy(this.availableDocuments);
    availableDocuments[index] = {
      ...item,
      titleLabel: updatedItem.title || updatedItem.label || ""
    };

    configStore.setAvailableDocuments(availableDocuments);
  }
}
